import { Icon, Table, Typography } from '@equinor/eds-core-react'
import { send } from '@equinor/eds-icons'
import { type FunctionComponent, useEffect, useState } from 'react'
import { pollingInterval } from '../../store/defaults'
import { type DeploymentSummary, useGetApplicationQuery } from '../../store/radix-api'
import { dataSorter, type SortDirection, sortCompareDate, sortCompareString } from '../../utils/sort-utils'
import { getNewSortDir, TableSortIcon } from '../../utils/table-sort-utils'
import { DeploymentSummaryTableRow } from './deployment-summary-table-row'

import './style.css'

export interface DeploymentListProps {
  appName: string
  deployments?: Readonly<Array<DeploymentSummary>>
  limit?: number
  inEnv?: boolean
  repository?: string
}

export const DeploymentList: FunctionComponent<DeploymentListProps> = ({
  appName,
  deployments,
  limit,
  inEnv,
  repository,
}) => {
  const [sortedData, setSortedData] = useState(deployments || [])
  const [dateSort, setDateSort] = useState<SortDirection>('descending')
  const [envSort, setEnvSort] = useState<SortDirection>()
  const [pipelineSort, setPipelineSort] = useState<SortDirection>()

  useEffect(() => {
    setSortedData(
      dataSorter(deployments?.slice(0, limit || deployments.length), [
        (x, y) => sortCompareDate(x.activeFrom, y.activeFrom, dateSort),
        (x, y) => sortCompareString(x.pipelineJobType, y.pipelineJobType, pipelineSort, false, () => !!pipelineSort),
        (x, y) => sortCompareString(x.environment, y.environment, envSort, false, () => !!envSort),
      ])
    )
  }, [dateSort, deployments, envSort, limit, pipelineSort])

  return (
    <div className="deployments-list grid grid--gap-medium">
      {sortedData.length > 0 ? (
        <div className="grid--table-overflow">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Cell>ID</Table.Cell>
                <Table.Cell sort="none" onClick={() => setDateSort(getNewSortDir(dateSort))}>
                  Date / Time
                  <TableSortIcon direction={dateSort} />
                </Table.Cell>
                {!inEnv && (
                  <>
                    <Table.Cell sort="none" onClick={() => setEnvSort(getNewSortDir(envSort, true))}>
                      Environment
                      <TableSortIcon direction={envSort} />
                    </Table.Cell>
                    <Table.Cell>Status</Table.Cell>
                  </>
                )}
                <Table.Cell sort="none" onClick={() => setPipelineSort(getNewSortDir(pipelineSort, true))}>
                  Type of job
                  <TableSortIcon direction={pipelineSort} />
                </Table.Cell>
                <Table.Cell>Github commit</Table.Cell>
                <Table.Cell>Promoted from</Table.Cell>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {sortedData.map((x, i) => (
                <DeploymentSummaryTableRow key={i} appName={appName} deployment={x} inEnv={inEnv} repo={repository} />
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : (
        <div className="deployments-list--empty">
          <span>
            <Icon data={send} />
          </span>
          <Typography>No deployments… yet</Typography>
        </div>
      )}
    </div>
  )
}

export interface DeploymentListContainerProps {
  appName: string
  deployments?: Readonly<Array<DeploymentSummary>>
  limit?: number
  inEnv?: boolean
}

export const DeploymentListContainer: FunctionComponent<DeploymentListContainerProps> = ({
  appName,
  deployments,
  limit,
  inEnv,
}) => {
  const { data } = useGetApplicationQuery({ appName }, { pollingInterval })
  const repository = data?.registration?.repository

  return (
    <DeploymentList appName={appName} deployments={deployments} limit={limit} inEnv={inEnv} repository={repository} />
  )
}
